import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { errors, paxRoute, type RouteAuth, StreamResponse, serviceRefusals } from "../../common";
import {
  AgentVersionParams,
  AgentVersionPin,
  GameAgentStateParams,
  GameMasterCasting,
  GameMemberParams,
  GameParams,
  JudgeLiveInputRequest,
  JudgeLiveInputResponse,
  LobbyCopy,
  MemberCasting,
  PlayerAdmission,
  PutAgentStateResponse,
  RecordAgentFailureRequest,
  RecordAgentFailureResponse,
  RecordCastingResponse,
  RecordCheckpointRequest,
  RecordCheckpointResponse,
  RecordGameMasterResponse,
  RecordLastOnlineRequest,
  RecordLastOnlineResponse,
  RecordLiveRequest,
  RecordLiveResponse,
  RecordLobbyResponse,
  SessionGame,
  Sha256BlobParams,
} from "./schemas";

const catalogReaderAuth: RouteAuth = {
  kind: "service",
  callers: { "game-server": "games:storage", "agent-runner": "catalog:read" },
};

const agentStateReaderAuth: RouteAuth = {
  kind: "service",
  callers: { "agent-runner": "agent-state:read" },
};

const agentStateWriterAuth: RouteAuth = {
  kind: "service",
  callers: { "agent-runner": "agent-state:write" },
};

const gameStorageAuth: RouteAuth = {
  kind: "service",
  callers: { "game-server": "games:storage" },
};

const liveModerationAuth: RouteAuth = {
  kind: "service",
  callers: { "game-server": "moderation:judge" },
};

const gameNotFound = {
  NOT_FOUND: { message: "The game or stored object does not exist." },
} as const;

export const getCatalogBlob = oc
  .meta(
    paxRoute({ auth: catalogReaderAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/internal/catalog/v1alpha1/blobs/{sha256}",
      operationId: "internal.catalog.blobs.get",
      summary: "Read a content-addressed catalog blob.",
      inputStructure: "detailed",
      outputStructure: "detailed",
      responseBodyHint: "octet-stream",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals, ...errors.notFound })
  .input(z.object({ params: Sha256BlobParams }))
  .output(StreamResponse);

export const getAgentVersionPin = oc
  .meta(
    paxRoute({ auth: catalogReaderAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/internal/catalog/v1alpha1/agent-versions/{agentVersionId}",
      operationId: "internal.catalog.agentVersions.get",
      summary: "Read where a published agent version's code is, and the file to run.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals, ...errors.notFound })
  .input(z.object({ params: AgentVersionParams }))
  .output(AgentVersionPin);

export const getAgentState = oc
  .meta(
    paxRoute({ auth: agentStateReaderAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/internal/games/v1alpha1/{id}/agent-state/{agentId}",
      operationId: "internal.games.agentState.get",
      summary: "Read one agent's state archive.",
      inputStructure: "detailed",
      outputStructure: "detailed",
      responseBodyHint: "octet-stream",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals, ...gameNotFound })
  .input(z.object({ params: GameAgentStateParams }))
  .output(StreamResponse);

export const setAgentState = oc
  .meta(
    paxRoute({ auth: agentStateWriterAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/internal/games/v1alpha1/{id}/agent-state/{agentId}",
      operationId: "internal.games.agentState.set",
      summary: "Store one agent's state archive.",
      inputStructure: "detailed",
      requestBodyHint: "octet-stream",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...serviceRefusals,
    ...gameNotFound,
    PAYLOAD_TOO_LARGE: { message: "The state archive exceeds 1 GB." },
  })
  .input(
    z.object({
      params: GameAgentStateParams,
      headers: z.object({
        "x-pax-content-length": z.coerce.number().int().nonnegative(),
      }),
      body: z.instanceof(ReadableStream),
    }),
  )
  .output(PutAgentStateResponse);

export const getGame = oc
  .meta(
    paxRoute({ auth: gameStorageAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/internal/games/v1alpha1/{id}",
      operationId: "internal.games.get",
      summary: "Read a game for its session: its kind, its preset, and everyone in it.",
      description:
        "The session reads it when its first connection opens, and again whenever the API says the game changed. Members come with their profiles, for the game's `members` frames.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals, ...gameNotFound })
  .input(z.object({ params: GameParams }))
  .output(SessionGame);

export const setCheckpoint = oc
  .meta(
    paxRoute({ auth: gameStorageAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/internal/games/v1alpha1/{id}/checkpoint",
      operationId: "internal.games.checkpoint.set",
      summary: "Record a checkpointed World revision.",
      description:
        "Keeps the greater of the stored and reported revisions and round counts, and rewrites every member's casting.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals, ...gameNotFound })
  .input(z.object({ params: GameParams, body: RecordCheckpointRequest }))
  .output(RecordCheckpointResponse);

export const setCasting = oc
  .meta(
    paxRoute({ auth: gameStorageAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/internal/games/v1alpha1/{id}/members/{userId}/casting",
      operationId: "internal.games.members.casting.set",
      summary: "Record which perspective one member plays.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals })
  .input(z.object({ params: GameMemberParams, body: MemberCasting }))
  .output(RecordCastingResponse);

export const setLastOnline = oc
  .meta(
    paxRoute({ auth: gameStorageAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/internal/games/v1alpha1/{id}/members/{userId}/last-online",
      operationId: "internal.games.members.lastOnline.set",
      summary: "Record when a member left the game.",
      description:
        "The game server sends it once a member's last connection has been closed for 30 seconds. Keeps the later of the stored and reported moments.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals })
  .input(z.object({ params: GameMemberParams, body: RecordLastOnlineRequest }))
  .output(RecordLastOnlineResponse);

export const setGameMaster = oc
  .meta(
    paxRoute({ auth: gameStorageAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/internal/games/v1alpha1/{id}/game-master",
      operationId: "internal.games.gameMaster.set",
      summary: "Record which agent version holds the game master role.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals })
  .input(z.object({ params: GameParams, body: GameMasterCasting }))
  .output(RecordGameMasterResponse);

export const setLobby = oc
  .meta(
    paxRoute({ auth: gameStorageAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/internal/games/v1alpha1/{id}/lobby",
      operationId: "internal.games.lobby.set",
      summary: "Record the current state of a game's lobby.",
      description:
        "Replaces the API's copy of the game's owner, roles, and members' roles, unless it already holds this lobby revision or a newer one.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals })
  .input(z.object({ params: GameParams, body: LobbyCopy }))
  .output(RecordLobbyResponse);

export const setLive = oc
  .meta(
    paxRoute({ auth: gameStorageAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/internal/games/v1alpha1/{id}/live",
      operationId: "internal.games.live.set",
      summary: "Record whether anyone is connected to a game.",
      description:
        "The game server sends it when the first connection to the game opens and when the last one closes. The API tells a live game when something it holds changes, and leaves a game nobody is connected to alone.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals })
  .input(z.object({ params: GameParams, body: RecordLiveRequest }))
  .output(RecordLiveResponse);

export const admitPlayer = oc
  .meta(
    paxRoute({ auth: gameStorageAuth, minimumTier: null, cost: "read" }),
    openapi({
      method: "POST",
      path: "/internal/games/v1alpha1/{id}/admission",
      operationId: "internal.games.admission",
      summary: "Check whether a signed-in account may connect to a game.",
      description:
        "Reads the account from the browser's sign-in cookie, which the game server passes on, and checks that it is not banned and is a member of the game.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals })
  .input(
    z.object({
      params: GameParams,
      headers: z.object({ "x-pax-cookie": z.string().optional() }),
    }),
  )
  .output(PlayerAdmission);

export const recordAgentFailure = oc
  .meta(
    paxRoute({ auth: gameStorageAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/internal/games/v1alpha1/{id}/agent-failures",
      operationId: "internal.games.agentFailures.create",
      summary: "Record that a grader or player agent stopped without finishing.",
      description:
        "Fails the evaluation trial playing the game, if one is still playing or grading, with the error.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...serviceRefusals })
  .input(z.object({ params: GameParams, body: RecordAgentFailureRequest }))
  .output(RecordAgentFailureResponse);

export const judgeLiveInput = oc
  .meta(
    paxRoute({ auth: liveModerationAuth, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/internal/moderation/v1alpha1/live-inputs",
      operationId: "internal.moderation.liveInputs.create",
      summary: "Judge one input a player sent in a live game, before other players see it.",
      description:
        "Automod classifies the input with its context, the live game policy redacts it or lets it through, and any points and ban land on the author. Returns what to do with the input and where the author stands on live games.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...serviceRefusals,
    NOT_FOUND: { message: "The game does not exist, or the author isn't a member of it." },
  })
  .input(JudgeLiveInputRequest)
  .output(JudgeLiveInputResponse);
